import React, { useState, useEffect, useRef } from "react";
import { X, ShoppingBag, ImageUp, Save, Plus } from "lucide-react";
import toast from "react-hot-toast";
import ProductImageUpload from "../admin/ProductImageUpload";
import VendorFormModal from "./VendorFormModal"; // Import Vendor Modal
import { api } from "../../api";
import SearchableSelect from "../common/SearchableSelect";
import "./ProductFormModal.css";

const CATEGORIES = ["ISF Shop", "Medicines", "Consumables", "Repairs", "Infra", "Others"];

// Product image: JPG or PNG (the upload endpoint's 5 MB limit).
const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const IMAGE_MAX_SIZE = 5 * 1024 * 1024;

const vendorIdOf = (entry) => String(entry?.vendorId?._id || entry?.vendorId || "");

// Rank 1 and Rank 2 always; Rank 3 only for a product that already has one.
const vendorSlotsOf = (product) => {
  const ranked = Array.isArray(product?.approvedVendors) ? product.approvedVendors : [];
  const idForRank = (rank) => vendorIdOf(ranked.find((v) => (v.rank || 1) === rank));
  const slots = [
    { vendorId: idForRank(1), rank: 1 },
    { vendorId: idForRank(2), rank: 2 },
  ];
  if (idForRank(3)) slots.push({ vendorId: idForRank(3), rank: 3 });
  return slots;
};

// onSubmit gets the product data, including `isActive` (the Status switch);
// the caller applies the switch after saving, since the create / update
// endpoints decide visibility from the category.
export default function ProductFormModal({
  product,
  onClose,
  onSubmit,
  onRefresh,
}) {
  const isEditing = Boolean(product);

  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [vendorsError, setVendorsError] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState(vendorSlotsOf(null));

  const [showVendorModal, setShowVendorModal] = useState(false); // State for Vendor Modal

  const [formData, setFormData] = useState({
    sku: "",
    name: "",
    description: "",
    category: "ISF Shop",
    price: "",
    discountPrice: "",
    maxPrice: "",
    stock: "",
    lowStockThreshold: "",
    imageUrl: "",
    isActive: true,
    isEssential: true,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const imageInputRef = useRef(null);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setVendorsLoading(true);
      setVendorsError(null);

      const response = await api.get("/api/v2/vendors", {
        params: {
          active: "true",
          limit: 100,
        },
      });

      if (response.data?.success) {
        setVendors(response.data.vendors || []);
      } else {
        throw new Error(response.data?.error || "Failed to load vendors");
      }
    } catch (err) {
      console.error("Failed to fetch vendors:", err);
      setVendors([]);
      setVendorsError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to load vendors",
      );
    } finally {
      setVendorsLoading(false);
    }
  };

  const handleCreateVendor = async (vendorData) => {
    try {
      const response = await api.post("/api/v2/vendors", vendorData);
      if (response.data.success) {
        toast.success("Vendor created successfully!");
        await fetchVendors(); // Refresh list
        setShowVendorModal(false);
        // Pick the new vendor in the first empty rank
        const newVendorId = response.data.vendor._id;
        setSelectedVendors((prev) => {
          const firstEmptyIndex = prev.findIndex((v) => !v.vendorId);
          if (firstEmptyIndex !== -1) {
            const next = [...prev];
            next[firstEmptyIndex] = {
              ...next[firstEmptyIndex],
              vendorId: newVendorId,
            };
            return next;
          }
          return prev;
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create vendor");
    }
  };

  useEffect(() => {
    if (product) {
      setFormData({
        sku: product.sku || "",
        name: product.name || "",
        description: product.description || "",
        category: product.category || "ISF Shop",
        price: product.price?.toString() || "",
        discountPrice: product.discountPrice?.toString() || "",
        maxPrice: product.maxPrice?.toString() || "",
        stock: product.stock?.toString() || "",
        lowStockThreshold: product.lowStockThreshold?.toString() || "",
        imageUrl: product.imageUrl || product.primaryImageUrl || "",
        isActive: Boolean(product.isActive),
        isEssential: product.isEssential !== false,
      });
    }
    setSelectedVendors(vendorSlotsOf(product));
  }, [product]);

  const clearError = (name) => {
    if (!errors[name]) return;
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleVendorChange = (index, vendorId) => {
    if (vendorId) {
      const isDuplicate = selectedVendors.some(
        (v, i) => i !== index && v.vendorId === vendorId,
      );
      if (isDuplicate) {
        toast.error("This vendor is already selected in another rank");
        return;
      }
    }

    setSelectedVendors((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], vendorId };
      return next;
    });
    clearError("approvedVendors");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      // Non-ISF Shop items only collect a single Price; clear the
      // ISF-Shop-only fields so stale values aren't submitted later.
      if (name === "category" && value !== "ISF Shop") {
        next.maxPrice = "";
        next.discountPrice = "";
      }
      return next;
    });
    clearError(name);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setErrors((prev) => ({ ...prev, imageUrl: "Use a JPG or PNG image" }));
      return;
    }
    if (file.size > IMAGE_MAX_SIZE) {
      setErrors((prev) => ({ ...prev, imageUrl: "Image is too large (max 5 MB)" }));
      return;
    }

    setUploadingImage(true);
    clearError("imageUrl");
    try {
      const body = new FormData();
      body.append("image", file);
      const response = await api.post("/api/v2/upload/image", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!response.data?.success || !response.data?.url) {
        throw new Error(response.data?.message || "Upload failed");
      }
      setFormData((prev) => ({ ...prev, imageUrl: response.data.url }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        imageUrl: err.response?.data?.message || err.message || "Image upload failed",
      }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    uploadImage(file);
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploadingImage || submitting) return;
    uploadImage(e.dataTransfer.files?.[0]);
  };

  const isIsfShop = formData.category === "ISF Shop";
  // Only ISF Shop items can be shown to students; an item that isn't set up
  // yet (Pending) can't be switched on.
  const statusLocked = !isIsfShop || Boolean(product?.isPendingProduct && !product?.isActive);
  const shownActive = isIsfShop && formData.isActive && !statusLocked;

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Product name is required";
    } else if (formData.name.length < 3 || formData.name.length > 100) {
      newErrors.name = "Name must be between 3 and 100 characters";
    }

    // Description (optional)
    if (formData.description.length > 500) {
      newErrors.description = "Description cannot exceed 500 characters";
    }

    // Selling rate (coins) / Price (₹ for other categories): required
    const price = parseInt(formData.price);
    if (!formData.price) {
      newErrors.price = isIsfShop ? "Selling rate is required" : "Price is required";
    } else if (isNaN(price) || price < 1) {
      newErrors.price = isIsfShop
        ? "Selling rate must be a positive whole number (coins)"
        : "Price must be a positive whole number";
    } else if (isIsfShop && formData.maxPrice && price > parseInt(formData.maxPrice)) {
      newErrors.price = "Selling rate cannot be greater than purchase rate";
    }

    // Purchase rate (required only for ISF Shop items)
    const maxPrice =
      formData.maxPrice === "" ? null : Number(formData.maxPrice);
    if (isIsfShop) {
      if (!isEditing) {
        if (maxPrice === null || Number.isNaN(maxPrice) || maxPrice < 0) {
          newErrors.maxPrice = "Purchase rate (₹) is required for ISF Shop items";
        }
      } else if (
        maxPrice !== null &&
        (Number.isNaN(maxPrice) || maxPrice < 0)
      ) {
        newErrors.maxPrice = "Purchase rate must be a non-negative number";
      }
    }

    // Discount price (kept from earlier edits; not on this form)
    if (formData.discountPrice) {
      const discountPrice = parseInt(formData.discountPrice);
      if (isNaN(discountPrice) || discountPrice < 0) {
        newErrors.price = "This product's discount price is invalid";
      } else if (formData.price && discountPrice > price) {
        newErrors.price = `Selling rate cannot be below the product's discount price (${discountPrice})`;
      }
    }

    // Stock quantity: required
    const stock = parseInt(formData.stock);
    if (formData.stock === "") {
      newErrors.stock = "Stock quantity is required";
    } else if (isNaN(stock) || stock < 0) {
      newErrors.stock = "Stock must be a non-negative number";
    }

    // Minimum stock quantity (optional)
    if (formData.lowStockThreshold) {
      const threshold = parseInt(formData.lowStockThreshold);
      if (isNaN(threshold) || threshold < 0) {
        newErrors.lowStockThreshold =
          "Minimum stock must be a non-negative number";
      }
    }

    // Approved vendors (required for new products)
    const approvedVendors = selectedVendors.filter((v) => v.vendorId);
    if (!isEditing && approvedVendors.length === 0) {
      newErrors.approvedVendors = "Please select at least one approved vendor";
    }

    // If editing an item that already has vendors, prevent clearing all
    if (
      isEditing &&
      Array.isArray(product?.approvedVendors) &&
      product.approvedVendors.length > 0 &&
      approvedVendors.length === 0
    ) {
      newErrors.approvedVendors = "At least one approved vendor is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const approvedVendors = selectedVendors
        .filter((v) => v.vendorId)
        .map((v) => ({ vendorId: v.vendorId, rank: v.rank }));

      const submitData = {
        ...formData,
        sku: formData.sku || undefined, // SKU isn't on the form; the backend handles a missing one
        description: formData.description.trim(),
        price: parseInt(formData.price),
        sellingPrice: parseInt(formData.price),
        discountPrice: isIsfShop && formData.discountPrice
          ? parseInt(formData.discountPrice)
          : null,
        maxPrice:
          isIsfShop && formData.maxPrice !== ""
            ? Number(formData.maxPrice)
            : undefined,
        stock: parseInt(formData.stock),
        lowStockThreshold: formData.lowStockThreshold
          ? parseInt(formData.lowStockThreshold)
          : 10,
        isActive: shownActive,
        isEssential: Boolean(formData.isEssential),
      };

      if (approvedVendors.length > 0) {
        submitData.approvedVendors = approvedVendors;
      }

      // Remove empty/null values (an edited product may clear its description)
      Object.keys(submitData).forEach((key) => {
        if ((submitData[key] === "" && !(key === "description" && isEditing)) || submitData[key] === null) {
          delete submitData[key];
        }
      });

      await onSubmit(submitData);
    } catch (error) {
      const apiErrors = error.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        const nextErrors = {};
        apiErrors.forEach((err) => {
          if (err?.field && err?.message && !nextErrors[err.field]) {
            nextErrors[err.field] = err.message;
          }
        });

        if (Object.keys(nextErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...nextErrors }));
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const errorOf = (name) => errors[name] && <p className="pfm-error">{errors[name]}</p>;
  const inputClass = (name) => (errors[name] ? "pfm-invalid" : "");
  const required = <span className="pfm-required">*</span>;
  const busy = submitting || uploadingImage;

  return (
    <>
      <div className="pfm-overlay" onClick={onClose}>
        <div
          className="pfm-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pfm-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="pfm-header">
            <span className="pfm-header-icon" aria-hidden="true">
              <ShoppingBag size={24} />
            </span>
            <div className="pfm-header-text">
              <h2 id="pfm-title">{isEditing ? "Edit Product" : "Add Product"}</h2>
              <p>{isEditing ? "Update this item's details." : "Add an item to the ISF Shop."}</p>
            </div>
            <button type="button" className="pfm-close" onClick={onClose} disabled={submitting} aria-label="Close">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="pfm-form" noValidate>
            <div className="pfm-body">
              <div className="pfm-top">
                <div className="pfm-details">
                  <h3 className="pfm-section-title">Product details</h3>

                  <div className="pfm-field">
                    <label htmlFor="pfm-name">Product name {required}</label>
                    <input
                      id="pfm-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={inputClass("name")}
                      placeholder="Enter product name"
                      disabled={submitting}
                    />
                    {errorOf("name")}
                  </div>

                  <div className="pfm-field">
                    <label htmlFor="pfm-category">Category {required}</label>
                    <select
                      id="pfm-category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      disabled={submitting}
                    >
                      {CATEGORIES.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div className="pfm-field">
                    <label htmlFor="pfm-description">Description (Optional)</label>
                    <textarea
                      id="pfm-description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                      className={inputClass("description")}
                      placeholder="Add a short product description..."
                      maxLength={500}
                      disabled={submitting}
                    />
                    {errorOf("description")}
                  </div>
                </div>

                <div className="pfm-side">
                  <h3 className="pfm-section-title">Product image</h3>
                  <div
                    className={`pfm-dropzone ${dragOver ? "pfm-dropzone-over" : ""} ${formData.imageUrl ? "pfm-dropzone-filled" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleImageDrop}
                  >
                    {formData.imageUrl ? (
                      <>
                        <img src={formData.imageUrl} alt="Product preview" className="pfm-preview" />
                        <div className="pfm-image-actions">
                          <button
                            type="button"
                            className="pfm-link"
                            onClick={() => imageInputRef.current?.click()}
                            disabled={busy}
                          >
                            {uploadingImage ? "Uploading..." : "Change"}
                          </button>
                          <button
                            type="button"
                            className="pfm-link pfm-link-danger"
                            onClick={() => setFormData((prev) => ({ ...prev, imageUrl: "" }))}
                            disabled={busy}
                          >
                            Remove
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="pfm-dropzone-btn"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={busy}
                      >
                        <ImageUp size={44} className="pfm-dropzone-icon" aria-hidden="true" />
                        <strong>{uploadingImage ? "Uploading..." : "Upload image"}</strong>
                        <span>
                          Drag &amp; drop or <span className="pfm-browse">browse</span>
                        </span>
                        <small>JPG or PNG</small>
                      </button>
                    )}
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept={IMAGE_TYPES.join(",")}
                      onChange={handleImagePick}
                      hidden
                    />
                  </div>
                  {errorOf("imageUrl")}

                  <h3 className="pfm-section-title">Status</h3>
                  <label className={`pfm-switch ${statusLocked ? "pfm-switch-locked" : ""}`}>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={shownActive}
                      onChange={handleChange}
                      disabled={submitting || statusLocked}
                    />
                    <span className="pfm-switch-track" aria-hidden="true" />
                    <span className="pfm-switch-text">
                      <strong>{shownActive ? "Active" : "Inactive"}</strong>
                      <small>
                        {!isIsfShop
                          ? "Only ISF Shop items are shown in the shop"
                          : statusLocked
                            ? "Finish setting up this item to show it"
                            : shownActive
                              ? "Visible in ISF Shop"
                              : "Hidden from ISF Shop"}
                      </small>
                    </span>
                  </label>

                  {isIsfShop && (
                    <>
                      <h3 className="pfm-section-title">Essential</h3>
                      <label className="pfm-switch">
                        <input
                          type="checkbox"
                          name="isEssential"
                          checked={Boolean(formData.isEssential)}
                          onChange={handleChange}
                          disabled={submitting}
                        />
                        <span className="pfm-switch-track" aria-hidden="true" />
                        <span className="pfm-switch-text">
                          <strong>{formData.isEssential ? "Essential" : "Not essential"}</strong>
                          <small>
                            {formData.isEssential
                              ? "Children can buy it"
                              : "Shown in the shop, but children can't buy it"}
                          </small>
                        </span>
                      </label>
                    </>
                  )}
                </div>
              </div>

              <h3 className="pfm-section-title">Rates &amp; stock</h3>
              <div className={`pfm-grid ${isIsfShop ? "pfm-grid-4" : "pfm-grid-3"}`}>
                {isIsfShop && (
                  <div className="pfm-field">
                    <label htmlFor="pfm-max-price">Purchase rate (₹) {!isEditing && required}</label>
                    <div className="pfm-prefixed">
                      <span aria-hidden="true">₹</span>
                      <input
                        id="pfm-max-price"
                        type="number"
                        name="maxPrice"
                        min="0"
                        value={formData.maxPrice}
                        onChange={handleChange}
                        className={inputClass("maxPrice")}
                        placeholder="Enter rupees"
                        disabled={submitting}
                      />
                    </div>
                    {errorOf("maxPrice")}
                  </div>
                )}

                <div className="pfm-field">
                  <label htmlFor="pfm-price">
                    {isIsfShop ? "Selling rate (ISF Coins)" : "Price (₹)"} {required}
                  </label>
                  <div className={`pfm-prefixed ${isIsfShop ? "pfm-prefixed-coin" : ""}`}>
                    <span aria-hidden="true">{isIsfShop ? <span className="pfm-coin">₹</span> : "₹"}</span>
                    <input
                      id="pfm-price"
                      type="number"
                      name="price"
                      min="1"
                      value={formData.price}
                      onChange={handleChange}
                      className={inputClass("price")}
                      placeholder={isIsfShop ? "Enter coins" : "Enter rupees"}
                      disabled={submitting}
                    />
                  </div>
                  {errorOf("price")}
                </div>

                <div className="pfm-field">
                  <label htmlFor="pfm-stock">Stock quantity {required}</label>
                  <input
                    id="pfm-stock"
                    type="number"
                    name="stock"
                    min="0"
                    value={formData.stock}
                    onChange={handleChange}
                    className={inputClass("stock")}
                    placeholder="Enter quantity"
                    disabled={submitting}
                  />
                  {errorOf("stock")}
                </div>

                <div className="pfm-field">
                  <label htmlFor="pfm-min-stock">Minimum stock quantity</label>
                  <input
                    id="pfm-min-stock"
                    type="number"
                    name="lowStockThreshold"
                    min="0"
                    value={formData.lowStockThreshold}
                    onChange={handleChange}
                    className={inputClass("lowStockThreshold")}
                    placeholder="Enter minimum"
                    disabled={submitting}
                  />
                  {errorOf("lowStockThreshold")}
                </div>
              </div>

              <div className="pfm-section-bar">
                <h3 className="pfm-section-title">Approved vendors</h3>
                <button
                  type="button"
                  className="pfm-link"
                  onClick={() => setShowVendorModal(true)}
                  disabled={submitting}
                >
                  <Plus size={14} aria-hidden="true" /> New vendor
                </button>
              </div>

              {vendorsLoading ? (
                <p className="pfm-note">Loading vendors...</p>
              ) : vendorsError ? (
                <p className="pfm-error">
                  {vendorsError}{" "}
                  <button type="button" className="pfm-link" onClick={fetchVendors}>Retry</button>
                </p>
              ) : (
                <>
                  <div className={`pfm-grid ${selectedVendors.length > 2 ? "pfm-grid-3" : "pfm-grid-2"}`}>
                    {selectedVendors.map((slot, index) => (
                      <div key={slot.rank} className="pfm-field">
                        <label htmlFor={`pfm-vendor-${slot.rank}`}>
                          Rank {slot.rank} {slot.rank === 1 && !isEditing && required}
                        </label>
                        <SearchableSelect
                          id={`pfm-vendor-${slot.rank}`}
                          searchPlaceholder="Search vendor..."
                          value={slot.vendorId}
                          onChange={(e) => handleVendorChange(index, e.target.value)}
                          className={inputClass("approvedVendors")}
                          disabled={submitting}
                        >
                          <option value="">Select approved vendor</option>
                          {vendors.map((v) => (
                            <option key={v._id} value={v._id}>
                              {v.name} ({v.phone})
                            </option>
                          ))}
                        </SearchableSelect>
                      </div>
                    ))}
                  </div>
                  {errorOf("approvedVendors")}
                  {vendors.length === 0 && (
                    <p className="pfm-note">
                      No active vendors found. Add one with "New vendor" before adding a new product.
                    </p>
                  )}
                </>
              )}

              {/* Story-14: more images for an existing product */}
              {isEditing && product._id && (
                <>
                  <h3 className="pfm-section-title">More product images</h3>
                  <ProductImageUpload
                    productId={product._id}
                    existingImages={product.images || []}
                    onUploadSuccess={onRefresh}
                  />
                </>
              )}
            </div>

            <div className="pfm-footer">
              <button type="button" className="pfm-btn-cancel" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="pfm-btn-save" disabled={busy}>
                <Save size={18} aria-hidden="true" />
                {submitting ? "Saving..." : isEditing ? "Save Changes" : "Save Product"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Inline Vendor Creation Modal */}
      {showVendorModal && (
        <VendorFormModal
          onClose={() => setShowVendorModal(false)}
          onSubmit={handleCreateVendor}
        />
      )}
    </>
  );
}
